import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Button, IconButton, TextField, Menu, MenuItem, ListItemText, CircularProgress, Tooltip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import FlexService from '../../services/FlexService';
import { matchesShowWhen, mapAction } from './workspaceUtils';

/**
 * FH-373: a day schedule -- one row per resource (a dock door), the day's open hours across in slots, and a block per
 * item (a trailer's appointment) at its time. Everything it shows comes from one FLEX Command (scheduleScript; not loadScript, which the generic grid loader would also run) and the
 * view's JSON; a free slot and a block's menu run the workspace's own actions, so a FORM or EXECUTE does what it does
 * on a grid.
 *
 * Block config (view JSON, type "schedule"):
 *   scheduleScript  -- called with _payload {from, to} (ISO); answers {timeZone, opens, closes, slotMinutes,
 *                      <rowsField>: [...], <itemsField>: [...]}
 *   rowsField, rowKey, rowLabel        -- the resources (doors) and their key / label fields
 *   itemsField, itemKey, itemRowField, itemStartField -- the items, which row they are on, when they start
 *   itemTitle, itemDetails[], colorField + colorMap {value: palette key}, lateWhen, fadeWhen (showWhen conditions)
 *   unassignedLabel -- a last row for items on no row ("No door yet")
 *   slotAction      -- run on a free slot with {<rowKey>: row, start: ISO}; its prefill maps those into the form
 *   itemActions[]   -- a block's menu (showWhen per action), run with the item as the row
 *   newLabel        -- the header button that runs slotAction with nothing filled in
 *   itemSubtitle    -- the field on a block's second line (the trailer's status), in words
 *   rowNoun         -- "appointment"
 *
 * Times: hours, slots and the day are the warehouse's (timeZone; the browser's when it has none). Each item lasts
 * slotMinutes from its start.
 */

const pad2 = (n) => String(n).padStart(2, '0');

// The wall-clock parts of a moment in a time zone
const zonedParts = (ms, timeZone) => {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZone || undefined, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = {};
  fmt.formatToParts(new Date(ms)).forEach((p) => { if (p.type !== 'literal') parts[p.type] = Number(p.value); });
  return parts;
};
const zonedDate = (ms, timeZone) => {
  const p = zonedParts(ms, timeZone);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
};
const zonedMinutes = (ms, timeZone) => {
  const p = zonedParts(ms, timeZone);
  return p.hour * 60 + p.minute;
};
// The moment a wall-clock time in a time zone is (2026-11-20 09:30 in America/New_York -> its UTC instant)
const zonedToInstant = (date, minutes, timeZone) => {
  const [y, m, d] = date.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d, Math.floor(minutes / 60), minutes % 60);
  const offsetAt = (ms) => {
    const p = zonedParts(ms, timeZone);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - ms;
  };
  let ms = guess - offsetAt(guess);
  ms = guess - offsetAt(ms);
  return ms;
};
const addDays = (date, n) => {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
};
const toMinutes = (hhmm, fallback) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : fallback;
};
const clock = (minutes) => `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
const dayLabel = (date) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};

const SLOT_WIDTH = 100; // wide enough for a whole trailer number
const ROW_HEIGHT = 56;
const LABEL_WIDTH = 132;

// the day a link names: a plain YYYY-MM-DD, or the day of a time it names (here)
// ARRIVED -> Arrived, NOT_STARTED -> Not started
const words = (v) => (v == null ? '' : String(v).replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()));

const askedDay = (asked) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(asked)) return asked;
  const ms = asked ? Date.parse(asked) : NaN;
  return Number.isNaN(ms) ? null : zonedDate(ms, undefined);
};

const SchedulePanel = ({ config, colors, context, refreshToken, onRowAction, onError }) => {
  const {
    scheduleScript, rowsField = 'rows', rowKey = 'id', rowLabel, itemsField = 'items', itemKey = 'id', itemRowField,
    itemStartField, itemTitle, itemDetails = [], colorField, colorMap = {}, lateWhen, fadeWhen, unassignedLabel,
    newLabel, rowNoun = 'item', title, itemSubtitle,
  } = config;
  // the same shape the grids hand to onRowAction (scriptName, formEntity, prefill...)
  const slotAction = useMemo(() => (config.slotAction ? mapAction(config.slotAction, 0, 'schedule-slot', 'SINGLE') : null), [config.slotAction]);
  const itemActions = useMemo(() => (config.itemActions || []).map((a, i) => mapAction(a, i, 'schedule-item', 'SINGLE')), [config.itemActions]);

  // the day opened: one a link asked for (a trailer's own appointment), else today here
  const askedDate = context?.date ? String(context.date) : '';
  const [date, setDate] = useState(() => askedDay(askedDate) || zonedDate(Date.now(), undefined));
  // the calendar may already be open when a link asks for another day
  useEffect(() => {
    const day = askedDay(askedDate);
    if (day) setDate(day);
  }, [askedDate]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menu, setMenu] = useState(null); // {anchor, item}
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // a day either side: the warehouse's day may start before or after the browser's; each item is placed below
      const from = new Date(zonedToInstant(addDays(date, -1), 0, undefined)).toISOString();
      const to = new Date(zonedToInstant(addDays(date, 2), 0, undefined)).toISOString();
      const response = await FlexService.executeScript({ scriptName: scheduleScript, context: { _payload: { from, to } } });
      if (!response.success || response.context?.error_message) {
        setData(null);
        if (onError) onError(response.context?.error_message || response.error || 'The schedule could not be loaded.');
        return;
      }
      setData(response.context?.result || null);
    } catch (err) {
      setData(null);
      if (onError) onError(err.message || 'The schedule could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [date, scheduleScript, onError]);

  useEffect(() => { load(); }, [load, refreshToken]);

  const timeZone = data?.timeZone || undefined;
  const slotMinutes = Math.max(5, Number(data?.slotMinutes) || 30);
  const opens = toMinutes(data?.opens, 6 * 60);
  const closes = Math.max(opens + slotMinutes, toMinutes(data?.closes, 20 * 60));
  const slots = useMemo(() => {
    const out = [];
    for (let m = opens; m + slotMinutes <= closes; m += slotMinutes) out.push(m);
    return out;
  }, [opens, closes, slotMinutes]);

  // the day's items, by row, placed at their minute of the warehouse's day
  const { rows, byRow, count, late } = useMemo(() => {
    const resources = Array.isArray(data?.[rowsField]) ? data[rowsField] : [];
    const items = Array.isArray(data?.[itemsField]) ? data[itemsField] : [];
    const keys = new Set(resources.map((r) => String(r[rowKey])));
    const map = {};
    let n = 0;
    let lateCount = 0;
    items.forEach((item) => {
      const ms = Date.parse(item[itemStartField]);
      if (Number.isNaN(ms) || zonedDate(ms, timeZone) !== date) return;
      const rowId = item[itemRowField] && keys.has(String(item[itemRowField])) ? String(item[itemRowField]) : '__none__';
      const start = zonedMinutes(ms, timeZone);
      const isLate = !!lateWhen && matchesShowWhen(item, lateWhen) && ms + slotMinutes * 60000 < now;
      if (isLate) lateCount += 1;
      n += 1;
      (map[rowId] = map[rowId] || []).push({ item, start, ms, isLate });
    });
    const list = resources.map((r) => ({ id: String(r[rowKey]), label: String(r[rowLabel || rowKey] ?? r[rowKey]), row: r }));
    if (unassignedLabel) list.push({ id: '__none__', label: unassignedLabel, row: null });
    return { rows: list, byRow: map, count: n, late: lateCount };
  }, [data, rowsField, itemsField, rowKey, rowLabel, itemStartField, itemRowField, unassignedLabel, lateWhen, timeZone, date, slotMinutes, now]);

  const isToday = date === zonedDate(now, timeZone);
  const nowMinute = zonedMinutes(now, timeZone);

  const runSlot = (row, minute) => {
    if (!slotAction || !onRowAction) return;
    const start = minute == null ? undefined : new Date(zonedToInstant(date, minute, timeZone)).toISOString();
    onRowAction({ ...slotAction, forceCreate: true }, { [rowKey]: row?.id && row.id !== '__none__' ? row.id : undefined, start });
  };

  const left = (minute) => ((minute - opens) / slotMinutes) * SLOT_WIDTH;
  const shownActions = menu ? itemActions.filter((a) => matchesShowWhen(menu.item, a.showWhen)) : [];

  return (
    <Box sx={{ border: `1px solid ${colors.border}`, bgcolor: colors.surface || colors.background }}>
      {/* the day, and the way to book */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 2, py: 1.5, borderBottom: `1px solid ${colors.border}` }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 1 }}>{title || 'Schedule'}</Typography>
        <IconButton size="small" aria-label="Previous day" onClick={() => setDate(addDays(date, -1))}><ChevronLeftIcon /></IconButton>
        <TextField type="date" size="small" value={date} onChange={(e) => e.target.value && setDate(e.target.value)}
          inputProps={{ 'aria-label': 'Day' }} sx={{ width: 170 }} />
        <IconButton size="small" aria-label="Next day" onClick={() => setDate(addDays(date, 1))}><ChevronRightIcon /></IconButton>
        <Button size="small" variant="outlined" onClick={() => setDate(zonedDate(Date.now(), timeZone))} disabled={isToday}>Today</Button>
        <Typography variant="body2" sx={{ color: colors.textMuted, ml: 1 }}>
          {dayLabel(date)} · {count} {count === 1 ? rowNoun : `${rowNoun}s`}{late ? ` · ${late} late` : ''}
          {data ? ` · ${clock(opens)}-${clock(closes)}${timeZone ? ` ${timeZone}` : ' (your time zone: the warehouse has none; set its Timezone on Warehouse Config so every screen and Atom agree)'}` : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {loading && <CircularProgress size={18} />}
        {slotAction && (
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => runSlot(null, null)}>
            {newLabel || 'New'}
          </Button>
        )}
      </Box>

      {/* rows across the day; scrolls sideways inside the panel, never the page */}
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: LABEL_WIDTH + slots.length * SLOT_WIDTH }}>
          <Box sx={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, position: 'sticky', top: 0, bgcolor: colors.surface || colors.background, zIndex: 2 }}>
            <Box sx={{ width: LABEL_WIDTH, flexShrink: 0, position: 'sticky', left: 0, bgcolor: 'inherit', zIndex: 3 }} />
            {slots.map((m) => (
              <Box key={m} sx={{ width: SLOT_WIDTH, flexShrink: 0, py: 0.75, textAlign: 'center', fontSize: '0.72rem', color: colors.textMuted, borderLeft: `1px solid ${colors.border}` }}>
                {clock(m)}
              </Box>
            ))}
          </Box>

          {data && rows.length === 0 && (
            <Typography variant="body2" sx={{ p: 3, color: colors.textMuted }}>
              There are no dock doors yet. Add them on the Dock Doors tab; trailers booked without a door show under {unassignedLabel || 'No door yet'}.
            </Typography>
          )}

          {rows.map((r) => (
            <Box key={r.id} data-testid="schedule-row" data-row={r.id} sx={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, height: ROW_HEIGHT }}>
              <Box sx={{ width: LABEL_WIDTH, flexShrink: 0, px: 1.5, display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: '0.85rem', position: 'sticky', left: 0, bgcolor: colors.surface || colors.background, zIndex: 1, borderRight: `1px solid ${colors.border}` }}>
                {r.label}
              </Box>
              <Box sx={{ position: 'relative', width: slots.length * SLOT_WIDTH, flexShrink: 0 }}>
                {/* the free slots: click one to book it (a slot a block covers is taken, so it is no button) */}
                {slots.filter((m) => !(byRow[r.id] || []).some(({ start }) => start < m + slotMinutes && m < start + slotMinutes)).map((m) => (
                  <Box key={m} component="button" type="button"
                    aria-label={`Book ${r.id === '__none__' ? 'without a door' : r.label} at ${clock(m)}`}
                    onClick={() => runSlot(r, m)}
                    sx={{ position: 'absolute', left: left(m), top: 0, width: SLOT_WIDTH, height: '100%', border: 0, borderLeft: `1px solid ${colors.border}`, bgcolor: 'transparent', cursor: slotAction ? 'pointer' : 'default', '&:hover': slotAction ? { bgcolor: 'action.hover' } : {} }} />
                ))}
                {isToday && nowMinute >= opens && nowMinute <= closes && (
                  <Box aria-hidden sx={{ position: 'absolute', left: left(nowMinute), top: 0, bottom: 0, width: 2, bgcolor: 'error.main', zIndex: 1, pointerEvents: 'none' }} />
                )}
                {(byRow[r.id] || []).map(({ item, start, isLate }) => {
                  const outside = start < opens || start + slotMinutes > closes;
                  const at = Math.min(Math.max(start, opens), closes - slotMinutes);
                  const faded = fadeWhen && matchesShowWhen(item, fadeWhen);
                  const palette = colorMap[String(item[colorField])] || 'primary';
                  const details = itemDetails.map((f) => item[f]).filter((v) => v !== null && v !== undefined && v !== '');
                  const label = `${item[itemTitle]} ${clock(start)}-${clock(start + slotMinutes)}${isLate ? ', late' : ''}${outside ? ', outside dock hours' : ''}`;
                  return (
                    <Tooltip key={String(item[itemKey])} title={`${label}${details.length ? ` · ${details.join(' · ')}` : ''}`}>
                      <Box component="button" type="button" data-testid="schedule-block" aria-label={label}
                        onClick={(e) => setMenu({ anchor: e.currentTarget, item })}
                        sx={{
                          position: 'absolute', left: left(at) + 2, top: 4, width: SLOT_WIDTH - 4, height: ROW_HEIGHT - 8, zIndex: 2,
                          border: '1px solid', borderColor: isLate ? 'error.main' : `${palette}.main`, borderLeftWidth: 4,
                          bgcolor: `${palette}.main`, color: `${palette}.contrastText`, opacity: faded ? 0.45 : 1,
                          textAlign: 'left', px: 0.5, overflow: 'hidden', cursor: 'pointer', fontSize: '0.7rem', lineHeight: 1.25,
                          outline: isLate ? '2px solid' : 'none', outlineColor: 'error.main', outlineOffset: -2,
                        }}>
                        <Box sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item[itemTitle]}</Box>
                        <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {isLate ? 'Late' : outside ? 'Outside hours' : itemSubtitle ? words(item[itemSubtitle]) : details[0] || ''}
                        </Box>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {data && count === 0 && rows.length > 0 && (
        <Typography variant="body2" sx={{ px: 2, py: 1.5, color: colors.textMuted }}>
          Nothing booked this day. Click a free slot to book a trailer.
        </Typography>
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)}>
        {shownActions.length === 0 && <MenuItem disabled><ListItemText primary="Nothing to do here" /></MenuItem>}
        {shownActions.map((a) => (
          <MenuItem key={a.actionId || a.label} onClick={() => { const item = menu.item; setMenu(null); onRowAction && onRowAction(a, item); }}>
            <ListItemText primary={a.label} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default SchedulePanel;
